import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageGray from '@/components/General/PageGray';
import H1 from '@/components/Typography/Headings/H1';
import P from '@/components/Typography/P';
import Span from '@/components/Typography/Span';
import Loader from '@/components/Loader/Loader';
import GeneralError from '@/components/Errors/General';
import PercentileChart, {
  type PercentileChartPoint,
} from '@/components/Charts/PercentileChart';
import RankingSimulationCard from '@/components/Graduatorie/RankingSimulationCard';
import { useGetRankings } from '@/hooks/rankings/useRanking';
import { useGetAttempts } from '@/hooks/attempts/useAttempts';
import { useGetCollections } from '@/hooks/collections/useCollections';
import { useTestContext } from '@/context/TestContext';
import { CollectionType } from '@/types/collection';
import { AttemptStatus } from '@/types/attempt';
import { Search } from '@testbusters/search';

export const Route = createFileRoute('/_sidebar/graduatorie/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { selectedTestId } = useTestContext();
  const [search, setSearch] = useState('');

  const {
    attempts,
    isFetching: isAttemptsFetching,
    hasNextPage: attemptsHasNextPage,
    fetchNextPage: attemptsFetchNextPage,
    isFetchingNextPage: attemptsIsFetchingNextPage,
    error: attemptsError,
  } = useGetAttempts({
    testId: selectedTestId,
    type: CollectionType.SIMULATION,
    status: AttemptStatus.COMPLETED,
  });

  const {
    collections,
    isFetching: isCollectionsFetching,
    hasNextPage: collectionsHasNextPage,
    fetchNextPage: collectionsFetchNextPage,
    isFetchingNextPage: collectionsIsFetchingNextPage,
    error: collectionsError,
  } = useGetCollections({
    testId: selectedTestId,
    type: CollectionType.SIMULATION,
  });

  useEffect(() => {
    if (attemptsHasNextPage && !attemptsIsFetchingNextPage)
      attemptsFetchNextPage();
  }, [attemptsHasNextPage, attemptsIsFetchingNextPage]);

  useEffect(() => {
    if (collectionsHasNextPage && !collectionsIsFetchingNextPage)
      collectionsFetchNextPage();
  }, [collectionsHasNextPage, collectionsIsFetchingNextPage]);

  const collectionMap = useMemo(
    () => new Map(collections.map((c) => [c._id, c])),
    [collections],
  );

  // All completed attempts, sorted oldest first
  const completedAttempts = useMemo(
    () =>
      attempts
        .filter((a) => a.status === AttemptStatus.COMPLETED)
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        ),
    [attempts],
  );

  // Chart: all completed attempts with a score
  const chartData = useMemo<PercentileChartPoint[]>(() => {
    return completedAttempts
      .filter((a) => a.stats?.totalScore != null)
      .map((a) => {
        const totalScore = a.stats!.totalScore!;
        const maxScore = a.maxScore ?? 100;
        const value =
          a.stats?.ranking ?? Math.round((totalScore / maxScore) * 100);
        return {
          date: a.createdAt,
          value,
          collectionName: collectionMap.get(a.collectionId)?.name ?? '—',
          totalScore,
          maxScore,
        };
      });
  }, [completedAttempts, collectionMap]);

  // Cards: first completed attempt per collection
  const cardItems = useMemo(() => {
    const seen = new Set<string>();
    const items: {
      attempt: (typeof completedAttempts)[0];
      collection: NonNullable<ReturnType<typeof collectionMap.get>>;
    }[] = [];
    for (const a of completedAttempts) {
      if (seen.has(a.collectionId)) continue;
      seen.add(a.collectionId);
      const collection = collectionMap.get(a.collectionId);
      if (collection) items.push({ attempt: a, collection });
    }
    return items;
  }, [completedAttempts, collectionMap]);

  const { rankingMap, isFetching: isRankingFetching } = useGetRankings(
    cardItems.map((item) => item.collection._id),
  );

  const filteredCards = useMemo(() => {
    if (!search.trim()) return cardItems;
    const q = search.toLowerCase();
    return cardItems.filter((item) =>
      item.collection.name.toLowerCase().includes(q),
    );
  }, [cardItems, search]);

  const isFetching = isAttemptsFetching || isCollectionsFetching;
  const error = attemptsError || collectionsError;

  if (error) return <GeneralError />;

  return (
    <PageGray className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <H1>{t('graduatorie.title')}</H1>
        <P>{t('graduatorie.subtitle')}</P>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-2xl p-7 flex flex-col gap-4 shadow-none">
        <div className="flex flex-col gap-1">
          <Span className="text-lg! font-semibold">
            {t('graduatorie.resultsTitle')}
          </Span>
          <P className="text-sm text-text-body-description">
            {t('graduatorie.resultsSubtitle')}
          </P>
        </div>

        {isFetching ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <img
              src="/illustrations/bad-score.svg"
              alt=""
              width={120}
            />
            <Span className="font-bold">{t('graduatorie.noData')}</Span>
            <P className="text-sm text-text-body-description text-center">
              {t('graduatorie.noDataDesc')}
            </P>
          </div>
        ) : (
          <PercentileChart data={chartData} />
        )}
      </div>

      {/* Cards list */}
      {!isFetching && cardItems.length > 0 && (
        <div className="flex flex-col gap-6">
          <div className="max-w-3/5">
            <Search
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search')}
              className="bg-white"
            />
          </div>
          <div className="flex flex-col gap-4">
            {filteredCards.map((item) => (
              <RankingSimulationCard
                key={item.collection._id}
                collection={item.collection}
                attempt={item.attempt}
                ranking={rankingMap.get(item.collection._id)}
                isFetchingRanking={isRankingFetching}
              />
            ))}
          </div>
        </div>
      )}
    </PageGray>
  );
}
